import { useCallback, useEffect, useState } from 'react';
import GoBack from 'components/Common/GoBack';
import Render from 'components/Common/Render';
import BusinessHeader from 'components/Home/BusinessHeader';
import { Loader } from 'components/Common/Loader';
import { ReactComponent as NoTransactionsImage } from 'assets/no_transactions.svg';
import { http } from 'middleware/apiMiddleware';
import { subscriptionHistoryList } from 'constants/apiUrls';
import { useAppSelector } from 'hooks/useRedux';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { RootState } from 'store';
import { toast } from 'react-hot-toast';
import moment from 'moment';

const TABLE_HEADERS = ['Date', 'Subscriber Name', 'Subscription Name', 'Subscription Amount'];

const formatUsd = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const formatDate = (date: string) => moment(date).format('MMM DD, YYYY');

function parseSubscriptionHistoryList(data: unknown): SubscriptionHistoryListItem[] {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data as SubscriptionHistoryListItem[];
  }
  const record = data as Record<string, unknown>;
  if (Array.isArray(record.results)) {
    return record.results as SubscriptionHistoryListItem[];
  }
  if (Array.isArray(record.data)) {
    return record.data as SubscriptionHistoryListItem[];
  }
  return [];
}

const SubscriptionHistory = () => {
  const businessState = useAppSelector((state: RootState) => state.businesses);
  const [selectedBusiness] = useLocalStorage('selectedBusiness', null);
  const business: BusinessType = businessState?.business || selectedBusiness || null;

  const [historyList, setHistoryList] = useState<SubscriptionHistoryListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async (businessUuid: string) => {
    try {
      setLoading(true);
      const { data, error } = await http.get<unknown>(subscriptionHistoryList(businessUuid));
      if (error) {
        toast.error('Failed to load subscription history');
        setHistoryList([]);
        return;
      }
      setHistoryList(parseSubscriptionHistoryList(data));
    } catch {
      toast.error('Unexpected error loading subscription history');
      setHistoryList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (business?.uuid) {
      fetchHistory(business.uuid);
    }
  }, [business?.uuid, fetchHistory]);

  return (
    <div className="bg-white h-screen flex flex-col overflow-hidden">
      <Render when={!!business}>
        <div className="pt-8 pb-2 px-6">
          <BusinessHeader selectedBusiness={business} />
        </div>
        <div className="flex-1 flex flex-col overflow-hidden bg-offWhite min-h-0">
          <div className="px-8 pt-5 pb-4 flex-shrink-0">
            <GoBack label="Subscription History" containerClass="!my-0 text-xl font-semibold" />
          </div>

          <div className="flex-1 overflow-hidden px-8 pb-8 flex flex-col gap-4 min-h-0">
            {loading && historyList.length === 0 ? (
              <div className="flex flex-1 justify-center items-center">
                <Loader size={6} color="darkBlue" />
              </div>
            ) : historyList.length === 0 && !loading ? (
              <div className="flex flex-col items-center">
                <NoTransactionsImage width={'100%'} />
                No subscription history yet.
              </div>
            ) : (
              <>
                <div className="md:block hidden flex-1 overflow-auto no-scrollbar">
                  <div className="bg-white">
                    <table className="w-full table-auto text-left">
                      <thead className="sticky top-0 bg-paleLilac">
                        <tr>
                          {TABLE_HEADERS.map((head) => (
                            <th key={head} className="border-b border-paleGrey p-4">
                              <h2 className="font-poppins font-medium text-sm leading-none tracking-normal text-grayMedium">
                                {head}
                              </h2>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {historyList.map((item, index) => {
                          const isLast = index === historyList.length - 1;
                          const classes = `font-poppins font-normal text-sm leading-none tracking-normal text-darkBlack bg-white ${isLast ? 'p-4' : 'p-4 border-b border-gray-100'}`;
                          return (
                            <tr key={item.uuid}>
                              <td className={classes}>{formatDate(item.billingPeriodStart)}</td>
                              <td className={classes}>{item.userName}</td>
                              <td className={classes}>{item.subscriptionTitle}</td>
                              <td className={classes}>{formatUsd(item.amount)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {loading && (
                      <div className="flex w-full mt-2 mb-2 items-center justify-center">
                        <Loader size={6} color="darkBlue" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:hidden flex py-2 flex-col overflow-auto relative">
                  {historyList.map((item) => (
                    <table key={item.uuid} className="bg-lightPrimary my-2 w-full">
                      <thead className="bg-lightPurple">
                        <tr>
                          <td className="px-4 py-2">{TABLE_HEADERS[0]}:</td>
                          <td className="px-4 py-2">{formatDate(item.billingPeriodStart)}</td>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="px-4 py-2">{TABLE_HEADERS[1]}:</td>
                          <td className="px-4 py-2">{item.userName}</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-2">{TABLE_HEADERS[2]}:</td>
                          <td className="px-4 py-2">{item.subscriptionTitle}</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-2">{TABLE_HEADERS[3]}:</td>
                          <td className="px-4 py-2">{formatUsd(item.amount)}</td>
                        </tr>
                      </tbody>
                    </table>
                  ))}
                  {loading && (
                    <div className="flex w-full mt-2 mb-2 items-center justify-center">
                      <Loader size={6} color="darkBlue" />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Render>
    </div>
  );
};

export default SubscriptionHistory;
